import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, LazyMotion, domAnimation, m } from "framer-motion";
import { sileo } from "sileo";
import supportTicketService from "@shared/api/supportTicketService";
import {
  isAuthenticated as checkAuth,
  getUserEmail,
  getUserId,
} from "../../utils/authStorage";
import "./SupportTicket.css";

// Helper para abrir el modal de soporte desde cualquier componente
export const openSupportTicket = () => {
  window.dispatchEvent(new CustomEvent("open-support-ticket"));
};

const TICKET_TYPES = [
  { value: "bug", label: "Bug / Error", icon: "fa-bug", color: "#e74c3c" },
  {
    value: "suggestion",
    label: "Sugerencia",
    icon: "fa-lightbulb",
    color: "#f39c12",
  },
  {
    value: "improvement",
    label: "Mejora",
    icon: "fa-rocket",
    color: "#3498db",
  },
  {
    value: "question",
    label: "Consulta",
    icon: "fa-question-circle",
    color: "#9b59b6",
  },
];

const PRIORITIES = [
  { value: "alta", label: "Alta", color: "#e74c3c" },
  { value: "media", label: "Media", color: "#f39c12" },
  { value: "baja", label: "Baja", color: "#2ecc71" },
];

const MODULES = [
  "Dashboard",
  "Inventario",
  "Reportes",
  "Formatos",
  "Equipos / Máquinas",
  "Mantenimiento",
  "Certificaciones",
  "Plan Anual",
  "Calendario",
  "Pendientes",
  "Herramientas",
  "Usuarios",
  "Plantas",
  "Perfil",
  "Otro",
];

const JAM_RECORDING_LINK = import.meta.env.VITE_JAM_RECORDING_LINK || "";

const SupportTicket = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [uiState, setUiState] = useState({
    formVisible: false,
    submitting: false,
    submitted: false,
    ticketUrl: "",
  });
  const formRef = useRef(null);

  const [form, setForm] = useState({
    type: "",
    priority: "",
    title: "",
    description: "",
    module: "",
    steps: "",
    expected: "",
    jamUrl: "",
  });

  // Cerrar con Escape
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape" && isOpen) {
        handleClose();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen]);

  // Bloquear scroll cuando está abierto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Escuchar evento personalizado para abrir desde Sidebar / Profile
  useEffect(() => {
    const handler = () => handleOpen();
    window.addEventListener("open-support-ticket", handler);
    return () => window.removeEventListener("open-support-ticket", handler);
  }, []);

  const handleClose = () => {
    setUiState((p) => ({ ...p, formVisible: false }));
    setTimeout(() => {
      setIsOpen(false);
      setUiState((p) => ({ ...p, submitted: false, ticketUrl: "" }));
      setForm({
        type: "",
        priority: "",
        title: "",
        description: "",
        module: "",
        steps: "",
        expected: "",
        jamUrl: "",
      });
    }, 300);
  };

  const handleOpen = () => {
    setIsOpen(true);
    setTimeout(() => setUiState((p) => ({ ...p, formVisible: true })), 50);
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const buildJamRecordingUrl = () => {
    if (!JAM_RECORDING_LINK) return "";

    try {
      const url = new URL(JAM_RECORDING_LINK);

      if (form.title?.trim()) {
        url.searchParams.set("jam-title", form.title.trim());
      }

      const userReference = getUserEmail() || `id-${getUserId() || "anon"}`;
      if (userReference) {
        url.searchParams.set("jam-reference", userReference);
      }

      return url.toString();
    } catch (error) {
      return JAM_RECORDING_LINK;
    }
  };

  const handleOpenJamRecorder = () => {
    const jamUrl = buildJamRecordingUrl();

    if (!jamUrl) {
      sileo.warning({
        title: "Jam no configurado",
        description:
          "Falta definir VITE_JAM_RECORDING_LINK en las variables de entorno.",
        duration: 4000,
      });
      return;
    }

    window.open(jamUrl, "_blank", "noopener,noreferrer");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.type || !form.priority || !form.title || !form.description) {
      sileo.warning({
        title: "Campos incompletos",
        description: "Completa el tipo, prioridad, título y descripción.",
        duration: 3000,
      });
      return;
    }

    setUiState((p) => ({ ...p, submitting: true }));

    try {
      const response = await supportTicketService.createTicket({
        ...form,
        jamUrl: form.jamUrl?.trim() || "",
      });
      setUiState((p) => ({
        ...p,
        ticketUrl: response.data?.data?.url || "",
        submitted: true,
      }));
      sileo.success({
        title: "Ticket enviado",
        description: "Tu solicitud fue registrada exitosamente.",
        duration: 4000,
      });
    } catch (error) {
      console.error("Error al enviar ticket:", error);
      sileo.error({
        title: "Error",
        description:
          error.response?.data?.message ||
          "No se pudo enviar el ticket. Intenta de nuevo.",
        duration: 4000,
      });
    } finally {
      setUiState((p) => ({ ...p, submitting: false }));
    }
  };

  const isBug = form.type === "bug";

  // Verificar auth
  if (!checkAuth()) return null;

  return (
    <>
      {/* Modal con formulario */}
      {isOpen &&
        createPortal(
          <AnimatePresence>
            {uiState.formVisible && (
              <LazyMotion features={domAnimation}>
                <m.div
                  className="support-backdrop"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={handleClose}
                >
                  <m.div
                    className="support-panel"
                    ref={formRef}
                    initial={{ opacity: 0, y: 40, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 40, scale: 0.95 }}
                    transition={{ type: "spring", damping: 25, stiffness: 300 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Header */}
                    <div className="support-header">
                      <div className="support-header-left">
                        <i className="fas fa-headset support-header-icon"></i>
                        <div>
                          <h2 className="support-title">Soporte Técnico</h2>
                          <p className="support-subtitle">
                            ¿Necesitas ayuda? Envía tu solicitud
                          </p>
                        </div>
                      </div>
                      <button className="support-close" onClick={handleClose}>
                        <i className="fas fa-times"></i>
                      </button>
                    </div>

                    {!uiState.submitted ? (
                      <form onSubmit={handleSubmit} className="support-form">
                        {/* Tipo de reporte */}
                        <div className="support-field">
                          <span className="support-label">
                            Tipo de solicitud{" "}
                            <span className="required">*</span>
                          </span>
                          <div className="support-type-grid">
                            {TICKET_TYPES.map((t) => (
                              <button
                                key={t.value}
                                type="button"
                                className={`support-type-card ${form.type === t.value ? "active" : ""}`}
                                style={{
                                  "--type-color": t.color,
                                }}
                                onClick={() => handleChange("type", t.value)}
                              >
                                <i className={`fas ${t.icon}`}></i>
                                <span>{t.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Prioridad */}
                        <div className="support-field">
                          <span className="support-label">
                            Prioridad <span className="required">*</span>
                          </span>
                          <div className="support-priority-row">
                            {PRIORITIES.map((p) => (
                              <button
                                key={p.value}
                                type="button"
                                className={`support-priority-btn ${form.priority === p.value ? "active" : ""}`}
                                style={{ "--priority-color": p.color }}
                                onClick={() =>
                                  handleChange("priority", p.value)
                                }
                              >
                                <span
                                  className="priority-dot"
                                  style={{ background: p.color }}
                                ></span>
                                {p.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Módulo */}
                        <div className="support-field">
                          <span className="support-label">Módulo afectado</span>
                          <select
                            className="support-select"
                            value={form.module}
                            onChange={(e) =>
                              handleChange("module", e.target.value)
                            }
                          >
                            <option value="">Seleccionar módulo...</option>
                            {MODULES.map((m) => (
                              <option key={m} value={m}>
                                {m}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Título */}
                        <div className="support-field">
                          <span className="support-label">
                            Título <span className="required">*</span>
                          </span>
                          <input
                            type="text"
                            className="support-input"
                            placeholder="Resumen breve del problema o solicitud"
                            value={form.title}
                            onChange={(e) =>
                              handleChange("title", e.target.value)
                            }
                            maxLength={150}
                          />
                        </div>

                        {/* Descripción */}
                        <div className="support-field">
                          <span className="support-label">
                            Descripción <span className="required">*</span>
                          </span>
                          <textarea
                            className="support-textarea"
                            placeholder="Describe detalladamente tu solicitud..."
                            value={form.description}
                            onChange={(e) =>
                              handleChange("description", e.target.value)
                            }
                            rows={4}
                          />
                        </div>

                        {/* Campos extra para bugs */}
                        {isBug && (
                          <>
                            <div className="support-field">
                              <span className="support-label">
                                Pasos para reproducir
                              </span>
                              <textarea
                                className="support-textarea"
                                placeholder="1. Ir a la sección de...&#10;2. Hacer clic en...&#10;3. Se muestra el error..."
                                value={form.steps}
                                onChange={(e) =>
                                  handleChange("steps", e.target.value)
                                }
                                rows={3}
                              />
                            </div>
                            <div className="support-field">
                              <span className="support-label">
                                Comportamiento esperado
                              </span>
                              <input
                                type="text"
                                className="support-input"
                                placeholder="¿Qué debería suceder normalmente?"
                                value={form.expected}
                                onChange={(e) =>
                                  handleChange("expected", e.target.value)
                                }
                              />
                            </div>

                            <div className="support-jam-box">
                              <div>
                                <p className="support-jam-title">Grabación con Jam</p>
                                <p className="support-jam-help">
                                  Abre el grabador, reproduce el error y pega aquí
                                  el enlace de Jam para adjuntarlo al ticket.
                                </p>
                              </div>
                              <button
                                type="button"
                                className="support-jam-btn"
                                onClick={handleOpenJamRecorder}
                              >
                                <i className="fas fa-video"></i>
                                Grabar con Jam
                              </button>
                            </div>

                            <div className="support-field">
                              <span className="support-label">URL de Jam (opcional)</span>
                              <input
                                type="url"
                                className="support-input"
                                placeholder="https://jam.dev/c/..."
                                value={form.jamUrl}
                                onChange={(e) =>
                                  handleChange("jamUrl", e.target.value)
                                }
                              />
                            </div>
                          </>
                        )}

                        {/* Botón enviar */}
                        <button
                          type="submit"
                          className="support-submit"
                          disabled={uiState.submitting}
                        >
                          {uiState.submitting ? (
                            <>
                              <i className="fas fa-spinner fa-spin"></i>
                              Enviando...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-paper-plane"></i>
                              Enviar ticket
                            </>
                          )}
                        </button>
                      </form>
                    ) : (
                      /* Confirmación de envío */
                      <div className="support-success">
                        <div className="support-success-icon">
                          <i className="fas fa-check-circle"></i>
                        </div>
                        <h3>¡Ticket enviado!</h3>
                        <p>
                          Tu solicitud fue registrada y se le dará seguimiento a
                          la brevedad.
                        </p>
                        {uiState.ticketUrl && (
                          <a
                            href={uiState.ticketUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="support-ticket-link"
                          >
                            <i className="fas fa-up-right-from-square"></i>
                            Ver ticket en Trello
                          </a>
                        )}
                        <button
                          className="support-submit"
                          onClick={handleClose}
                          style={{ marginTop: "1rem" }}
                        >
                          Cerrar
                        </button>
                      </div>
                    )}
                  </m.div>
                </m.div>
              </LazyMotion>
            )}
          </AnimatePresence>,
          document.body,
        )}
    </>
  );
};

export default SupportTicket;
